import snakeCase from 'lodash/snakeCase'
export function camelToKebab(str: string) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2') // 在小写字母和大写字母之间插入短横线
    .toLowerCase() // 将整个字符串转换为小写
}
export function getFileName(url: string) {
  // 去掉查询参数和哈希部分
  const cleanUrl = url.split('?')[0].split('#')[0]

  // 匹配文件名（不包括路径和扩展名）
  const matches = cleanUrl.match(/([^/]+)(?=\.\w+$)/)

  // 返回文件名，如果没有匹配到则返回默认值
  return matches ? matches[0] : 'default'
}

export function customSnakeCase(str: string, separator: string = '-') {
  return snakeCase(str).replace(/_/g, separator)
}

/**
 * Parses a string or number into a valid number, returning null if invalid.
 * @param value - The value to parse (string or number)
 * @returns A valid number or null if the input is invalid
 */
export const parseNumber = (value: string | number): number | null => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value
  return parsed && !isNaN(parsed) ? parsed : null
}

export const tryToBuf = (val: unknown): ArrayBuffer => {
  if (val instanceof ArrayBuffer) return val
  if (ArrayBuffer.isView(val)) {
    const buffer = (val as ArrayBufferView).buffer
    return buffer instanceof ArrayBuffer ? buffer : new ArrayBuffer(buffer.byteLength)
  }
  const s = String(val ?? '')
  try {
    return b64urlToArrayBuffer(s)
  } catch {
    return new TextEncoder().encode(s).buffer as ArrayBuffer
  }
}

const normalizeTransports = (arr?: any[]): AuthenticatorTransport[] | undefined => {
  if (!Array.isArray(arr) || arr.length === 0) return undefined
  const map: Record<string, AuthenticatorTransport | undefined> = {
    usb: 'usb',
    nfc: 'nfc',
    ble: 'ble',
    internal: 'internal',
    hybrid: 'hybrid',
    'smart-card': 'smart-card' as AuthenticatorTransport,
    smartcard: 'smart-card' as AuthenticatorTransport,
    cable: 'hybrid',
    passkeys: undefined, // 非标准值丢弃
  }
  const out = arr
    .map((v) => map[String(v).toLowerCase()])
    .filter(Boolean) as AuthenticatorTransport[]
  return out.length ? out : undefined
}

export const b64urlToArrayBuffer = (b64url: string): ArrayBuffer => {
  if (!b64url || typeof b64url !== 'string') {
    throw new Error('Invalid base64url input: must be a non-empty string')
  }
  const cleanInput = b64url.trim()

  // 长度 %4 == 1 的字符串不可能是合法 base64/base64url（否则需要 3 个 '='），提前报错更易定位
  if (cleanInput.length % 4 === 1) {
    throw new Error('Invalid base64/base64url input: bad length (mod 4 == 1)')
  }

  // 允许 base64url 和普通 base64 字符集（含 + / =）
  if (!/^[A-Za-z0-9\-_+/=]*$/.test(cleanInput)) {
    throw new Error('Invalid base64/base64url input: contains invalid characters')
  }

  try {
    // 把 url 变体（- _）统一映射到标准 base64（+ /），并按需补齐 '='
    const pad = '='.repeat((4 - (cleanInput.length % 4)) % 4)
    const base64 = (cleanInput + pad).replace(/-/g, '+').replace(/_/g, '/')
    const raw = atob(base64)
    const buf = new ArrayBuffer(raw.length)
    const view = new Uint8Array(buf)
    for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
    return buf
  } catch (error) {
    console.error('b64urlToArrayBuffer decode error:', error, 'input:', cleanInput)
    throw new Error(
      `Failed to decode base64/base64url: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}

export const arrayBufferToB64url = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

export function toPublicKeyOptions(json: any): PublicKeyCredentialRequestOptions {
  // 兼容 allowCredentials 缺省/非数组；id 既可能是 base64(url) 字符串、也可能是 ArrayBuffer
  const allow = (Array.isArray(json.allowCredentials) ? json.allowCredentials : []).map(
    (c: any) => ({
      type: (c?.type as PublicKeyCredentialType) || 'public-key',
      id: tryToBuf(c?.id),
      transports: normalizeTransports(c?.transports),
    }),
  )

  return {
    // challenge 可能是 base64(url) 或纯文本数字，这里统一宽容转换
    challenge: tryToBuf(String(json.challenge)),
    rpId: json.rpId || undefined,
    allowCredentials: allow.length ? allow : undefined,
    userVerification: (json.userVerification as UserVerificationRequirement) || 'preferred',
    timeout: typeof json.timeout === 'number' ? json.timeout : undefined,
    // extensions 必须是对象；数组/空壳则不传
    extensions: json.extensions && !Array.isArray(json.extensions) ? json.extensions : undefined,
    // ⚠️ 不要把 mediation 放在 publicKey 里；它应作为 credentials.get({ publicKey, mediation }) 顶层参数传入
  }
}

export const isLocalhost = () => ['localhost', '127.0.0.1'].includes(location.hostname)
